import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { api } from './services/api';
import { handleUnauthorized } from './services/sessionGuard';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { RequirePlatformAdmin } from './components/auth/RequirePlatformAdmin';
import { MainLayout } from './components/layout';
import { ErrorBoundary, RouteFallback } from './components/common';
import {
  AppPlaceholder,
  Approvals,
  Dashboard,
  History,
  Launcher,
  Login,
  NotFound,
  PlatformAdmin,
  PlatformSettings,
  Settings,
  Trading,
} from './pages';
import AppAccessGuard from './components/AppAccessGuard';
import AppAdminBySlug from './components/AppAdminBySlug';
import AppSlugIndex from './components/AppSlugIndex';
import TradingAgentShell from './components/layout/TradingAgentShell';
import { ThemeBodyClass } from '@shared/platform/components/ThemeBodyClass';
import { withChunkReload } from './lib/chunkReload';

// Every non-trading-agent app is loaded on demand (FR-032 / T062): none of
// them may be reachable from the login page or launcher's static import
// graph, since SurfTime/LocalTea/BitzBuggy pull in recharts + maplibre-gl.
// LocalTeaApp/LocalTeaAdmin (and the BitzBuggy/LLMChat pairs below) share
// one dynamic import() specifier each, so the bundler emits exactly one
// chunk per app regardless of how many named exports are lazy-wrapped.
// `components/AppAdminBySlug.tsx` is the other importer of these same
// `@/apps/*` specifiers (it lazy-loads each app's Admin export for the
// shared `/apps/:appSlug/admin` route) — the invariant that matters is that
// both call sites resolve to the SAME module specifier per app, since Vite
// only emits one chunk per resolved module regardless of how many places
// lazy() it.
//
// `withChunkReload` (021 fix-wave, user finding, live): a tab left open
// across a redeploy holds a stale asset manifest, so its next lazy import
// 404s on a chunk file that no longer exists — wrapped here, on every
// loader, so any app's lazy route recovers with the standard reload-once
// behavior rather than crashing straight to the error boundary.
const LLMChatApp = lazy(
  withChunkReload(() => import('./apps/llm-chat').then((m) => ({ default: m.LLMChatApp }))),
);
const LLMChatMemoryPage = lazy(
  withChunkReload(() => import('./apps/llm-chat').then((m) => ({ default: m.LLMChatMemoryPage }))),
);
const SmartNewsApp = lazy(
  withChunkReload(() => import('./apps/smart-news').then((m) => ({ default: m.SmartNewsApp }))),
);
const SurfTimeApp = lazy(
  withChunkReload(() => import('./apps/surftime').then((m) => ({ default: m.SurfTimeApp }))),
);
const LocalTeaApp = lazy(
  withChunkReload(() => import('./apps/localtea').then((m) => ({ default: m.LocalTeaApp }))),
);
const BitzBuggyApp = lazy(
  withChunkReload(() => import('./apps/bitzbuggy').then((m) => ({ default: m.BitzBuggyApp }))),
);

interface ProtectedRouteProps {
  children: React.ReactNode;
}

// Guard logic is mirrored in __tests__/integration/login-deep-link-batching.integration.test.tsx
// (PublicRoute isn't exported); keep the two in sync when changing this redirect.
function PublicRoute({ children }: ProtectedRouteProps) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const user = useAuthStore((state) => state.user);
  return isAuthenticated || user ? <Navigate to="/" replace /> : <>{children}</>;
}

/**
 * Trading Agent sub-routes live here (not on the shared `apps/:appSlug`
 * level) so `/apps/surftime/settings` and `/apps/smart-news/settings`
 * don't accidentally fall through to the trading agent `settings` route.
 * Every path inside this component is `trading-agent`-specific.
 */
function TradingAgentRoutes() {
  return (
    <Routes>
      <Route element={<TradingAgentShell />}>
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="trading" element={<Trading />} />
        <Route path="history" element={<History />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="settings" element={<Settings />} />
        {/* US7 tab bar points here; pages not yet built. */}
        <Route path="strategies" element={<AppPlaceholder />} />
        <Route path="discovery" element={<AppPlaceholder />} />
      </Route>
    </Routes>
  );
}

/**
 * Catch-all under /apps/:appSlug/* — hands off to the sub-app whose
 * internal <Routes> knows how to match the remaining segments. This lets
 * URLs like `/apps/surftime/map` resolve without requiring a redundant
 * `/surftime/surftime/map` path segment in the tab links.
 */
function AppSlugDispatcher() {
  const { appSlug } = useParams<{ appSlug: string }>();
  // trading-agent stays eager — it's core platform functionality, not a
  // code-split app, and carries no chart/map dependency of its own.
  if (appSlug === 'trading-agent') return <TradingAgentRoutes />;
  let content: React.ReactElement | null = null;
  if (appSlug === 'surftime') content = <SurfTimeApp />;
  else if (appSlug === 'localtea') content = <LocalTeaApp />;
  else if (appSlug === 'bitzbuggy') content = <BitzBuggyApp />;
  else return <NotFound />;
  return <Suspense fallback={<RouteFallback />}>{content}</Suspense>;
}

/** When on a platform-theme route, add class to html so CSS can lock viewport (no scrollbar shift). */
function PlatformThemeViewport() {
  const { pathname } = useLocation();
  const isPlatformTheme =
    pathname === '/login' || pathname === '/' || pathname === '/apps' || pathname === '/admin' || pathname === '/settings';

  useEffect(() => {
    const html = document.documentElement;
    if (isPlatformTheme) {
      html.classList.add('platform-theme-viewport');
    } else {
      html.classList.remove('platform-theme-viewport');
    }
    return () => html.classList.remove('platform-theme-viewport');
  }, [isPlatformTheme]);

  return null;
}

export default function App() {
  // A 401 from any query never hard-logs-out on its own. It revalidates the
  // session once (single-flight, in sessionGuard) and only redirects to /login
  // when that revalidation confirms the session is genuinely dead — so a stray
  // 401 from one endpoint can't tear down an otherwise-valid session.
  useEffect(() => {
    api.setOnUnauthorized(() => {
      void handleUnauthorized();
    });
    return () => api.setOnUnauthorized(null);
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ThemeBodyClass />
        <PlatformThemeViewport />
        <Routes>
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute>
                <MainLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Launcher />} />
            <Route path="apps" element={<Launcher />} />
            <Route path="settings" element={<PlatformSettings />} />
            <Route
              path="admin"
              element={
                <RequirePlatformAdmin>
                  <PlatformAdmin />
                </RequirePlatformAdmin>
              }
            />
            <Route path="apps/:appSlug" element={<AppAccessGuard />}>
              <Route index element={<AppSlugIndex />} />
              <Route
                path="chat/*"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <LLMChatApp />
                  </Suspense>
                }
              />
              {/* NerdyBot settings entry (PR #85 review, F2): the user-level
                  memory page, open to everyone — not admin-gated. Sits under
                  the generic apps/:appSlug route, so LLMChatMemoryPage
                  guards against foreign slugs itself. */}
              <Route
                path="memory"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <LLMChatMemoryPage />
                  </Suspense>
                }
              />
              <Route
                path="news/*"
                element={
                  <Suspense fallback={<RouteFallback />}>
                    <SmartNewsApp />
                  </Suspense>
                }
              />
              <Route
                path="admin"
                element={
                  <RequirePlatformAdmin>
                    <AppAdminBySlug />
                  </RequirePlatformAdmin>
                }
              />
              {/* Everything else — trading-agent sub-pages (dashboard,
                  settings, approvals, …), surftime (map, cams, beach/:slug,
                  settings), and localtea (map, explore, event/:id, settings) —
                  routes through the dispatcher so each app's sub-paths only
                  match when the slug matches. Prevents cross-app collisions
                  (e.g. `/apps/surftime/settings` no longer falls through to
                  Trading Agent's settings page). */}
              <Route path="*" element={<AppSlugDispatcher />} />
            </Route>
            {/* T037: old paths 404 (no redirect) */}
            <Route path="dashboard" element={<NotFound />} />
            <Route path="trading" element={<NotFound />} />
            <Route path="history" element={<NotFound />} />
            <Route path="settings" element={<NotFound />} />
            <Route path="admin" element={<NotFound />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
